// ============================================================
// api.js — Cliente de API de Vimo
// ============================================================

let API_BASE = '/api';

if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  if (window.location.port === '3000') {
    API_BASE = 'http://localhost:4001/api';
  }
}

export function getUploadsUrl() {
  return API_BASE.replace('/api', '/uploads');
}

export function sanitizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.includes('/uploads/')) {
    const filename = url.substring(url.lastIndexOf('/uploads/') + 9);
    return `${getUploadsUrl()}/${filename}`;
  }
  if (url.startsWith('http://') && window.location.protocol === 'https:') {
    return url.replace('http://', 'https://');
  }
  return url;
}

// Helper to get tokens
export function getAccessToken() {
  return localStorage.getItem('vimo_access_token');
}

export function getRefreshToken() {
  return localStorage.getItem('vimo_refresh_token');
}

export function saveTokens(accessToken, refreshToken) {
  localStorage.setItem('vimo_access_token', accessToken);
  localStorage.setItem('vimo_refresh_token', refreshToken);
}

export function clearTokens() {
  localStorage.removeItem('vimo_access_token');
  localStorage.removeItem('vimo_refresh_token');
  localStorage.removeItem('vimo_user');
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem('vimo_user'));
  } catch {
    return null;
  }
}

export function saveUser(user) {
  localStorage.setItem('vimo_user', JSON.stringify(user));
}

// Request interceptor/wrapper
export async function apiRequest(path, options = {}) {
  options.headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = getAccessToken();
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(`${API_BASE}${path}`, options);

  if (response.status === 401 && path !== '/auth/login' && path !== '/auth/register') {
    // Try to refresh token
    const refresh = getRefreshToken();
    if (refresh) {
      try {
        const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          saveTokens(data.accessToken, data.refreshToken);
          
          // Retry original request with new token
          options.headers['Authorization'] = `Bearer ${data.accessToken}`;
          response = await fetch(`${API_BASE}${path}`, options);
        } else {
          // Refresh failed
          clearTokens();
          window.location.href = 'login.html';
          throw new Error('Sesión expirada');
        }
      } catch (err) {
        clearTokens();
        window.location.href = 'login.html';
        throw err;
      }
    } else {
      // No refresh token, redirect
      clearTokens();
      window.location.href = 'login.html';
      throw new Error('No autorizado');
    }
  }

  if (!response.ok) {
    let errorMsg = `Error HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.error) errorMsg = errorData.error;
      else if (errorData.message) errorMsg = errorData.message;
      else if (errorData.errors?.length) errorMsg = errorData.errors.join(', ');
    } catch {
      const textErr = await response.text().catch(() => '');
      if (textErr) errorMsg = textErr.substring(0, 150);
    }
    throw new Error(errorMsg);
  }

  return response.json();
}

/**
 * Genera una imagen llamando al backend.
 */
export async function generateImage(payload) {
  console.log('[API] generateImage →', payload);
  
  let uploadedAttachments = [];
  if (payload.attachments && payload.attachments.length > 0) {
    uploadedAttachments = await Promise.all(
      payload.attachments.map(file => uploadAttachment(file))
    );
  }

  return apiRequest('/generate/image', {
    method: 'POST',
    body: JSON.stringify({
      prompt: payload.prompt,
      model: payload.model,
      aspect: payload.aspect,
      quality: payload.quality,
      count: payload.count,
      attachments: uploadedAttachments.map(a => a.url),
    }),
  });
}

/**
 * Genera un vídeo (Kling / Seedance / etc).
 */
export async function generateVideo(payload) {
  console.log('[API] generateVideo →', payload);

  let uploadedAttachments = [];
  if (payload.attachments && payload.attachments.length > 0) {
    uploadedAttachments = await Promise.all(
      payload.attachments.map(file => uploadAttachment(file))
    );
  }

  return apiRequest('/generate/video', {
    method: 'POST',
    body: JSON.stringify({
      prompt: payload.prompt,
      model: payload.model,
      aspect: payload.aspect,
      duration: payload.duration,
      audio: payload.audio !== false,
      attachments: uploadedAttachments.map(a => a.url),
    }),
  });
}

/**
 * Genera un vídeo de avatar hablante.
 */
export async function generateAvatar(payload) {
  console.log('[API] generateAvatar →', payload);

  let uploadedAttachments = [];
  if (payload.attachments && payload.attachments.length > 0) {
    uploadedAttachments = await Promise.all(
      payload.attachments.map(file => uploadAttachment(file))
    );
  }

  return apiRequest('/generate/avatar', {
    method: 'POST',
    body: JSON.stringify({
      text: payload.text || payload.prompt,
      avatarId: payload.avatarId,
      voiceId: payload.voiceId,
      aspect: payload.aspect,
      attachments: uploadedAttachments.map(a => a.url),
    }),
  });
}

/**
 * Sube un fichero adjunto.
 */
export async function uploadAttachment(file) {
  if (typeof file === 'string') {
    return { url: file, name: file.split('/').pop(), id: file };
  }

  // Validación previa en cliente antes de enviar
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
  if (file.size && file.size > MAX_FILE_SIZE) {
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`El archivo "${file.name}" pesa ${sizeInMB} MB, superando el límite máximo permitido de 100 MB.`);
  }

  console.log('[API] uploadAttachment →', file.name);

  const formData = new FormData();
  formData.append('file', file);

  const token = getAccessToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    const refresh = getRefreshToken();
    if (refresh) {
      const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });

      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        saveTokens(data.accessToken, data.refreshToken);
        
        headers['Authorization'] = `Bearer ${data.accessToken}`;
        response = await fetch(`${API_BASE}/upload`, {
          method: 'POST',
          headers,
          body: formData,
        });
      } else {
        clearTokens();
        window.location.href = 'login.html';
        throw new Error('Sesión expirada');
      }
    } else {
      clearTokens();
      window.location.href = 'login.html';
      throw new Error('No autorizado');
    }
  }

  if (!response.ok) {
    let errorMessage = '';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.errors?.join(', ');
    } catch {
      const text = await response.text().catch(() => '');
      if (response.status === 413) {
        errorMessage = 'Nginx rechazó el archivo por tamaño (Error 413 Payload Too Large). Ejecuta "systemctl reload nginx" en el servidor.';
      } else {
        errorMessage = `Error ${response.status} (${response.statusText}): ${text.slice(0, 150) || 'Respuesta no válida del servidor'}`;
      }
    }
    throw new Error(errorMessage || 'Error al subir el archivo');
  }

  return response.json();
}

/**
 * Polling de estado de un job.
 */
export async function getJobStatus(jobId) {
  console.log('[API] getJobStatus →', jobId);
  return apiRequest(`/jobs/${jobId}`);
}

/**
 * Lista el historial de sesiones del usuario.
 */
export async function listHistory() {
  console.log('[API] listHistory');
  return apiRequest('/generations');
}

/**
 * Elimina una generación por ID.
 */
export async function deleteGeneration(id) {
  console.log('[API] deleteGeneration →', id);
  return apiRequest(`/generations/${id}`, { method: 'DELETE' });
}

/**
 * Carga una sesión concreta para retomarla en la página correspondiente.
 */
export async function loadSession(sessionId) {
  console.log('[API] loadSession →', sessionId);
  return apiRequest(`/generations/${sessionId}`);
}

/**
 * Inicia sesión de usuario.
 */
export async function loginUser(email, password) {
  const data = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  saveTokens(data.accessToken, data.refreshToken);
  saveUser(data.user);
  return data;
}

/**
 * Registra un nuevo usuario.
 */
export async function registerUser(email, password, name) {
  const data = await apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
  saveTokens(data.accessToken, data.refreshToken);
  saveUser(data.user);
  return data;
}

export async function recoverPassword(email) {
  return apiRequest('/auth/recover', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Inicia sesión con OAuth social (Google Identity Services / OAuth).
 */
export async function loginSocial(providerOrData, token, extra = {}) {
  let body = {};
  if (typeof providerOrData === 'object') {
    body = providerOrData;
  } else if (typeof providerOrData === 'string' && token && typeof token === 'string' && !providerOrData.includes('@')) {
    body = { provider: providerOrData, token, ...extra };
  } else {
    // Compatibilidad: loginSocial(email, name, provider)
    body = { email: providerOrData, name: token, provider: extra.provider || 'social' };
  }

  const data = await apiRequest('/auth/social', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  saveTokens(data.accessToken, data.refreshToken);
  saveUser(data.user);
  return data;
}

/**
 * Restablece la contraseña de usuario usando un token.
 */
export async function resetPassword(email, token, password) {
  return apiRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, token, password }),
  });
}

/**
 * Cierra la sesión del usuario.
 */
export async function logoutUser() {
  const refresh = getRefreshToken();
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
  } catch (err) {
    // Ignorar error al cerrar sesión
  }
  clearTokens();
  window.location.href = 'login.html';
}

export const apiConfig = { API_BASE };
